import React from "react";
import {
  Dialog,
  Box,
  Typography,
  IconButton,
  Button,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import {
  getContentAccessUrl,
  downloadContentFile,
  markPlanItemViewed,
  markPlanItemReachedEnd,
} from "../../api/backend";

const loadPdfjs = () =>
  import("pdfjs-dist").then((pdfjsLib) => {
    pdfjsLib.GlobalWorkerOptions.workerSrc = new URL("pdfjs-dist/build/pdf.worker.min.mjs", import.meta.url).toString();
    return pdfjsLib;
  });

const T = {
  ink: "var(--color-ink)",
  ink4: "var(--color-ink-4)",
  line: "var(--color-hairline-cloud)",
};

function detectKind(item) {
  const name = (item?.fileName || item?.title || "").toLowerCase();
  const url = (item?.url || "").toLowerCase();
  if (name.endsWith(".pdf") || url.endsWith(".pdf")) return "pdf";
  if (/\.(png|jpe?g|gif|webp|svg)$/.test(name) || /\.(png|jpe?g|gif|webp|svg)$/.test(url)) return "image";
  if (/\.(doc|docx|ppt|pptx|xls|xlsx)$/.test(name)) return "office";
  if (/\.(txt|md|csv|json)$/.test(name)) return "text";
  return "office";
}

/** Renders a PDF's pages as canvases and reports when the last page is visible. */
function PdfViewer({ fileUrl, onReachedEnd }) {
  const containerRef = React.useRef(null);
  const [numPages, setNumPages] = React.useState(0);
  const [error, setError] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const reachedRef = React.useRef(false);

  React.useEffect(() => {
    let cancelled = false;
    let pdfDoc = null;

    loadPdfjs().then((pdfjsLib) => pdfjsLib.getDocument(fileUrl).promise)
      .then((doc) => {
        if (cancelled) return;
        pdfDoc = doc;
        setNumPages(doc.numPages);
        return Promise.all(
          Array.from({ length: doc.numPages }, (_, i) => i + 1).map((pageNum) =>
            doc.getPage(pageNum).then((page) => {
              const viewport = page.getViewport({ scale: 1.3 });
              const canvas = document.createElement("canvas");
              canvas.width = viewport.width;
              canvas.height = viewport.height;
              canvas.style.display = "block";
              canvas.style.margin = "0 auto 12px";
              canvas.style.maxWidth = "100%";
              canvas.dataset.pageNumber = String(pageNum);
              const ctx = canvas.getContext("2d");
              return page.render({ canvasContext: ctx, viewport }).promise.then(() => canvas);
            }),
          ),
        );
      })
      .then((canvases) => {
        if (cancelled || !canvases) return;
        const container = containerRef.current;
        if (!container) return;
        container.innerHTML = "";
        canvases.forEach((c) => container.appendChild(c));
        setLoading(false);

        const lastCanvas = canvases[canvases.length - 1];
        if (lastCanvas) {
          const observer = new IntersectionObserver(
            (entries) => {
              entries.forEach((entry) => {
                if (entry.isIntersecting && !reachedRef.current) {
                  reachedRef.current = true;
                  onReachedEnd();
                }
              });
            },
            { threshold: 0.6 },
          );
          observer.observe(lastCanvas);
          return () => observer.disconnect();
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err?.message || "Failed to load PDF");
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
      if (pdfDoc) pdfDoc.destroy();
    };
  }, [fileUrl, onReachedEnd]);

  if (error) {
    return (
      <Box sx={{ p: 4, textAlign: "center", color: T.ink4 }}>
        <Typography>Unable to render this PDF: {error}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ height: "100%", overflow: "auto", bgcolor: "#525659", p: 2 }}>
      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress size={28} sx={{ color: "#fff" }} />
        </Box>
      )}
      <Box ref={containerRef} />
      {numPages > 0 && !loading && (
        <Typography sx={{ textAlign: "center", color: "#ddd", fontSize: 12, mt: 1 }}>
          {numPages} page{numPages === 1 ? "" : "s"}
        </Typography>
      )}
    </Box>
  );
}

/** Renders plain text/HTML content in a scrollable box, detecting scroll-to-bottom. */
function ScrollTrackedViewer({ fileUrl, kind, onReachedEnd }) {
  const [content, setContent] = React.useState(null);
  const reachedRef = React.useRef(false);

  React.useEffect(() => {
    if (kind !== "text") return;
    fetch(fileUrl)
      .then((r) => r.text())
      .then(setContent)
      .catch(() => setContent("Unable to load document content."));
  }, [fileUrl, kind]);

  const handleScroll = (e) => {
    if (reachedRef.current) return;
    const el = e.target;
    if (el.scrollHeight - el.scrollTop <= el.clientHeight + 40) {
      reachedRef.current = true;
      onReachedEnd();
    }
  };

  if (kind === "image") {
    return (
      <Box
        onScroll={handleScroll}
        sx={{ height: "100%", overflow: "auto", bgcolor: "#000", display: "flex", justifyContent: "center" }}
        onLoad={() => onReachedEnd()}
      >
        <img
          src={fileUrl}
          alt=""
          style={{ maxWidth: "100%", height: "auto" }}
          onLoad={() => {
            if (!reachedRef.current) {
              reachedRef.current = true;
              onReachedEnd();
            }
          }}
        />
      </Box>
    );
  }

  return (
    <Box
      onScroll={handleScroll}
      sx={{ height: "100%", overflow: "auto", p: 3, bgcolor: "#fff", whiteSpace: "pre-wrap", fontSize: 14 }}
    >
      {content == null ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress size={28} />
        </Box>
      ) : (
        content
      )}
    </Box>
  );
}

/** Office formats have no reliable in-app scroll signal (cross-origin iframe) — manual affirmation only. */
function OfficeViewer({ fileUrl, title, onMarkComplete, completed }) {
  return (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <Box sx={{ flex: 1 }}>
        <iframe
          title={title}
          src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(fileUrl)}`}
          style={{ width: "100%", height: "100%", border: "none" }}
        />
      </Box>
      <Box sx={{ p: 1.5, borderTop: `1px solid ${T.line}`, display: "flex", justifyContent: "center" }}>
        <Button variant="contained" disabled={completed} onClick={onMarkComplete}>
          {completed ? "Marked as read" : "Mark as read"}
        </Button>
      </Box>
    </Box>
  );
}

/**
 * In-app document viewer for a Virtual Learning DOCUMENT item.
 * Fires markPlanItemViewed on open, and markPlanItemReachedEnd when the
 * student reaches the true end of the document (scroll-to-bottom / last PDF
 * page / manual affirmation for office formats).
 */
export default function VirtualLearningDocumentViewer({ open, item, courseId, planId, onClose, onCompleted }) {
  const [fileUrl, setFileUrl] = React.useState(null);
  const [error, setError] = React.useState(null);
  const [reached, setReached] = React.useState(false);
  const kind = React.useMemo(() => detectKind(item?.content || item), [item]);

  React.useEffect(() => {
    if (!open || !item) return;
    setFileUrl(null);
    setError(null);
    setReached(false);

    markPlanItemViewed(courseId, planId, item.id).catch(() => {});

    const contentId = item.contentRefId ?? item.content?.id;
    getContentAccessUrl(contentId)
      .then((url) => {
        if (/^https?:\/\//i.test(url)) return url;
        return downloadContentFile(contentId);
      })
      .then(setFileUrl)
      .catch(() => setError("Failed to load document."));
  }, [open, item, courseId, planId]);

  const handleReachedEnd = React.useCallback(() => {
    if (reached) return;
    setReached(true);
    markPlanItemReachedEnd(courseId, planId, item.id)
      .then(() => onCompleted && onCompleted(item))
      .catch(() => {});
  }, [reached, courseId, planId, item, onCompleted]);

  if (!item) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth PaperProps={{ sx: { height: "88vh" } }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", p: "10px 16px", borderBottom: `1px solid ${T.line}` }}>
        <Typography sx={{ fontSize: 14, fontWeight: 600, color: T.ink }}>{item.title}</Typography>
        <IconButton size="small" onClick={onClose}>
          <CloseIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Box>
      <Box sx={{ flex: 1, overflow: "hidden" }}>
        {error ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <Typography color="error">{error}</Typography>
          </Box>
        ) : !fileUrl ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress size={28} />
          </Box>
        ) : kind === "pdf" ? (
          <PdfViewer fileUrl={fileUrl} onReachedEnd={handleReachedEnd} />
        ) : kind === "text" || kind === "image" ? (
          <ScrollTrackedViewer fileUrl={fileUrl} kind={kind} onReachedEnd={handleReachedEnd} />
        ) : (
          <OfficeViewer fileUrl={fileUrl} title={item.title} completed={reached} onMarkComplete={handleReachedEnd} />
        )}
      </Box>
    </Dialog>
  );
}
